import {
  useEffect,
  useMemo,
  useState,
  cloneElement,
  isValidElement,
  Children,
  type ReactNode,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, message, Upload, UploadProps, Col, Row, Typography, Collapse } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import { MessageType } from 'models/MessageType';
import { hasUploadedFile } from 'components/form';
import { RcFile } from 'antd/es/upload';
import uploudlistIcon from 'assets/images/support/uploudlist.svg';
import image from 'assets/images/Image file.svg';
import { GetDocumentResponse, useGetDocumentForCI, useGetLifeDoc } from 'all-in-one-shared-api';
import downfile from 'assets/images/MakeClaim/downfile.svg';
const { Text } = Typography;
const { Panel } = Collapse;
import { isLocaleEnglish } from 'utils';
import PdfFileIcon from 'assets/images/PDFfileicon.svg';

const TEN_MB = 10 * 1024 * 1024;
const MAX_PER_FILE_SIZE = TEN_MB;
const MAX_SIZE = TEN_MB * 5;
const MAX_NUMBER_OF_ATTACHMENTS = 12;

const ANT_FILE_UID_PREFIX = 'rc-upload';

// 递归移除 antd 上传列表项中的删除按钮及其操作区（用于不可删除的服务端文件）
const stripRemoveAction = (node: ReactNode): ReactNode => {
  if (!isValidElement(node)) {
    return node;
  }
  const element = node as ReactElement<{ className?: string; children?: ReactNode }>;
  const className = element.props?.className;
  if (typeof className === 'string') {
    const shouldRemove =
      className.includes('ant-upload-list-item-remove') ||
      className.includes('ant-upload-list-item-actions') ||
      className.includes('ant-upload-list-item-card-actions');
    if (shouldRemove) {
      return null;
    }
  }
  const children = element.props?.children;
  if (children === undefined) {
    return element;
  }
  return cloneElement(
    element,
    element.props,
    Children.map(children, (child) => stripRemoveAction(child)),
  );
};

type DocumentsCardConfirmType = {
  setInvalid: (invalid: boolean) => void;
  onClaimFormPartIIChange?: (files: UploadFile[]) => void;
  onIdentityProofChange?: (files: UploadFile[]) => void;
  onLabReportsChange?: (files: UploadFile[]) => void;
  onOtherDocumentsChange?: (files: UploadFile[]) => void;
  initialClaimFormPartIIFiles?: UploadFile[];
  initialIdentityProofFiles?: UploadFile[];
  initialLabReportsFiles?: UploadFile[];
  initialOtherDocumentsFiles?: UploadFile[];
  token?: string;
};

const DocumentUploadFieldConfirm = ({
  name,
  label,
  setInvalid,
  onChange,
  initialFiles,
  maxSize,
  onPreview,
  extra,
  exceedCount,
}: {
  name: string;
  label: string;
  setInvalid: (invalid: boolean) => void;
  onChange?: (files: UploadFile[]) => void;
  initialFiles?: UploadFile[];
  maxSize: number;
  onPreview?: (file: UploadFile) => void;
  extra?: ReactNode;
  exceedCount: boolean;
}) => {
  const { t } = useTranslation();

  const [exceedSize, setExceedSize] = useState(false);
  const [attachments, setAttachments] = useState<UploadFile[]>(initialFiles || []);

  // 当 initialFiles 变化时（如接口数据加载完成后），同步到内部状态
  useEffect(() => {
    setAttachments(initialFiles || []);
  }, [initialFiles]);

  useEffect(() => {
    setInvalid(exceedSize || exceedCount);
  }, [exceedSize, exceedCount, setInvalid]);

  const attachmentUploadProps: UploadProps = useMemo(
    () => ({
      fileList: attachments,
      listType: 'picture',
      multiple: false,
      accept: 'image/*,application/pdf',
      onChange: ({ fileList }) => {
        const totalSize = fileList.map((a) => a.size || 0).reduce((acc, cur) => acc + cur, 0);
        setExceedSize(totalSize > maxSize);
        setAttachments(fileList);
        onChange?.(fileList);
      },

      beforeUpload: (f) => {
        if (hasUploadedFile(attachments as RcFile[], f)) {
          message[MessageType.ERROR](t('common.error.notAllowUploadSameDocument'));
          return Upload.LIST_IGNORE;
        }

        if (f.size > MAX_PER_FILE_SIZE) {
          message[MessageType.ERROR](t('common.error.sizeExceeds', { amount: 10 }));
          return Upload.LIST_IGNORE;
        }
        // 检查文件类型
        if (f.type.indexOf('image/') !== 0 && f.type !== 'application/pdf') {
          message[MessageType.ERROR](t('common.error.notPdfOrImages'));
          return Upload.LIST_IGNORE;
        }
        return false;
      },
      onPreview: (file) => {
        const uid = file.uid || '';
        // 本地新上传的文件：使用 blob URL 预览
        if (uid.startsWith(ANT_FILE_UID_PREFIX) && file.originFileObj) {
          const url = URL.createObjectURL(file.originFileObj as Blob);
          window.open(url, '_blank');
          return;
        }
        // 服务端文件：委托父组件通过 useGetDocument 获取 presigned URL
        onPreview?.(file);
      },
    }),
    [attachments, t, onChange, maxSize, onPreview],
  );

  return (
    <Col xs={24} md={24} style={{ marginBottom: '16px' }}>
      <div
        style={{
          color: '#6f7072',
          fontWeight: 'normal',
          fontSize: '17px',
          fontFamily: 'Roboto400',
          marginBottom: extra ? 0 : '8px',
        }}
      >
        {label}
      </div>
      {extra}
      <Upload
        className="customUpload"
        name={name}
        itemRender={(originNode, file) => {
          // 初始化的服务端文件（非本地新上传）不显示删除图标，且不可删除
          const isServerFile = !file.uid?.startsWith(ANT_FILE_UID_PREFIX) || !file.originFileObj;
          return isServerFile ? stripRemoveAction(originNode) : originNode;
        }}
        {...attachmentUploadProps}
        iconRender={() => <img src={image} width={24} height={24} alt="upload list icon" />}
      >
        <div className="buttoncontent buttoncontent-ci-upload" style={{ display: 'flex', justifyContent: 'center' }}>
          <Button className="button" disabled={exceedCount}>
            {t('enquiry.upload')}
          </Button>
        </div>
        <div
          style={{
            color: '#B0B2B3',
            fontWeight: 'normal',
            fontSize: '17px',
            fontFamily: 'Roboto400',
            marginTop: '20px',
          }}
        >
          <img src={uploudlistIcon} style={{ margin: '0  10px 5px 0', width: '24px', height: '24px' }} />
          {t('life.enquiry.uploadedAttr')}
        </div>
      </Upload>
      {exceedSize && <Text type="danger">{t('common.error.sizeExceeds', { amount: 10 })}</Text>}
    </Col>
  );
};

export const DocumentsCardForCIConfirm = ({
  setInvalid,
  onClaimFormPartIIChange,
  onIdentityProofChange,
  onLabReportsChange,
  onOtherDocumentsChange,
  initialClaimFormPartIIFiles,
  initialIdentityProofFiles,
  initialLabReportsFiles,
  initialOtherDocumentsFiles,
  token,
}: DocumentsCardConfirmType) => {
  const { t, i18n } = useTranslation();

  const [claimFormPartIIFiles, setClaimFormPartIIFiles] = useState<UploadFile[]>(initialClaimFormPartIIFiles || []);
  const [identityProofFiles, setIdentityProofFiles] = useState<UploadFile[]>(initialIdentityProofFiles || []);
  const [labReportsFiles, setLabReportsFiles] = useState<UploadFile[]>(initialLabReportsFiles || []);
  const [otherDocumentsFiles, setOtherDocumentsFiles] = useState<UploadFile[]>(initialOtherDocumentsFiles || []);

  // 预览窗口的 key，每次预览时刷新
  const [previewFileKey, setPreviewFileKey] = useState('');
  const [previewFilePath, setPreviewFilePath] = useState('');
  const { shouldFetch: GetLifeDocFetch, data: GetLifeDocFetchData } = useGetLifeDoc('useGetLifeDoc', token || '');

  // 服务端文件：通过 useGetDocument 获取 presigned URL 并在新窗口打开
  const { shouldFetch: previewShouldFetch } = useGetDocumentForCI(
    { key: previewFileKey },
    token ?? '',
    previewFilePath,
    {
      onSuccess: (data: GetDocumentResponse) => {
        window.open(data.presignedUrl, '_blank');
        previewShouldFetch(false);
        setPreviewFilePath('');
        setPreviewFileKey('');
      },
      onError: (err) => {
        if (err?.message?.includes('File not found')) {
          message[MessageType.ERROR](t('common.error.fileNotFound'));
        } else {
          message[MessageType.ERROR](t('common.error.generic'));
        }
        previewShouldFetch(false);
        setPreviewFilePath('');
        setPreviewFileKey('');
      },
    },
  );

  useEffect(() => {
    if (previewFilePath && previewFileKey) {
      previewShouldFetch(true);
    }
  }, [previewFilePath, previewFileKey, previewShouldFetch]);

  // 点击服务端文件预览：使用 uid（完整路径）作为 filePath 发起请求
  const handlePreviewServerFile = (file: UploadFile) => {
    const filePath = file.uid || '';
    if (filePath && !filePath.startsWith(ANT_FILE_UID_PREFIX)) {
      setPreviewFilePath(filePath);
      setPreviewFileKey(`preview-${Date.now()}`);
    }
  };

  const totalFiles = useMemo(() => {
    return (
      claimFormPartIIFiles.length + identityProofFiles.length + labReportsFiles.length + otherDocumentsFiles.length
    );
  }, [claimFormPartIIFiles, identityProofFiles, labReportsFiles, otherDocumentsFiles]);

  const totalSize = useMemo(() => {
    const getSize = (files: UploadFile[]) => files.reduce((acc, f) => acc + (f.size || 0), 0);
    return (
      getSize(claimFormPartIIFiles) +
      getSize(identityProofFiles) +
      getSize(labReportsFiles) +
      getSize(otherDocumentsFiles)
    );
  }, [claimFormPartIIFiles, identityProofFiles, labReportsFiles, otherDocumentsFiles]);

  useEffect(() => {
    setInvalid(totalFiles > MAX_NUMBER_OF_ATTACHMENTS || totalSize > MAX_SIZE);
  }, [totalFiles, totalSize, setInvalid]);

  const handleClaimFormPartIIChange = (files: UploadFile[]) => {
    setClaimFormPartIIFiles(files);
    onClaimFormPartIIChange?.(files);
  };

  const handleIdentityProofChange = (files: UploadFile[]) => {
    setIdentityProofFiles(files);
    onIdentityProofChange?.(files);
  };

  const handleLabReportsChange = (files: UploadFile[]) => {
    setLabReportsFiles(files);
    onLabReportsChange?.(files);
  };

  const handleOtherDocumentsChange = (files: UploadFile[]) => {
    setOtherDocumentsFiles(files);
    onOtherDocumentsChange?.(files);
  };
  useEffect(() => {
    GetLifeDocFetch(true);
  }, [GetLifeDocFetch]);
  return (
    <Row justify="center" gutter={[16, { xs: 8, sm: 8, lg: 24 }]}>
      <Col xs={24} md={24}>
        <div
          className="speakheader"
          style={{ paddingLeft: 0, fontFamily: 'Roboto400', fontSize: '22px', color: '#20252b', marginTop: '20px' }}
        >
          {t('life.enquiry.Supplementary')}
        </div>
      </Col>

      <DocumentUploadFieldConfirm
        name="criticalIllnessClaimFormAttachments"
        label={t('life.submitClaims.claimFormPartII')}
        setInvalid={() => {
          //bak
        }}
        onChange={handleClaimFormPartIIChange}
        initialFiles={initialClaimFormPartIIFiles}
        maxSize={MAX_SIZE - (totalSize - claimFormPartIIFiles.reduce((acc, f) => acc + (f.size || 0), 0))}
        onPreview={handlePreviewServerFile}
        exceedCount={totalFiles >= MAX_NUMBER_OF_ATTACHMENTS}
        extra={
          <div style={{ width: '100%', maxWidth: '100%' }}>
            <Collapse
              accordion
              className="Collapse downloadHeaderCollapse"
              expandIconPosition="end"
              style={{ width: '100%', marginTop: 0 }}
            >
              <Panel
                header={
                  <div className="Downloadheader Downloadheader--no-border-top" style={{ maxWidth: '100%' }}>
                    <div>
                      <img src={downfile} style={{ margin: '0  10px 5px 0', width: '24px', height: '24px' }} />
                      {t('life.MakeClaim.DownloadAClaimForm')}
                    </div>
                  </div>
                }
                key="1"
              >
                {GetLifeDocFetchData?.criticalIllness
                  ?.filter((item) => item.docOrder != '10')
                  ?.map((item, index) => {
                    return (
                      <div key={index}>
                        <a
                          key={index}
                          href={isLocaleEnglish(i18n.language) ? item.docUrlEn || '' : item.docUrlTc || ''}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <div>
                            <img src={PdfFileIcon} style={{ margin: '0  10px 5px 0', width: '24px', height: '24px' }} />
                            <span style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                              {isLocaleEnglish(i18n.language) ? item.docDescEn || '' : item.docDescTc || ''}
                            </span>
                          </div>
                        </a>
                      </div>
                    );
                  })}
              </Panel>
            </Collapse>
          </div>
        }
      />

      <DocumentUploadFieldConfirm
        name="proofOfIdentityAttachments"
        label={t('life.submitClaims.identityProof')}
        setInvalid={() => {
          //bak
        }}
        onChange={handleIdentityProofChange}
        initialFiles={initialIdentityProofFiles}
        maxSize={MAX_SIZE - (totalSize - identityProofFiles.reduce((acc, f) => acc + (f.size || 0), 0))}
        onPreview={handlePreviewServerFile}
        exceedCount={totalFiles >= MAX_NUMBER_OF_ATTACHMENTS}
      />

      <DocumentUploadFieldConfirm
        name="laboratoryAndPathologicalAttachments"
        label={t('life.submitClaims.labReports')}
        setInvalid={() => {
          //bak
        }}
        onChange={handleLabReportsChange}
        initialFiles={initialLabReportsFiles}
        maxSize={MAX_SIZE - (totalSize - labReportsFiles.reduce((acc, f) => acc + (f.size || 0), 0))}
        onPreview={handlePreviewServerFile}
        exceedCount={totalFiles >= MAX_NUMBER_OF_ATTACHMENTS}
      />

      <DocumentUploadFieldConfirm
        name="otherAttachments"
        label={t('life.submitClaims.otherDocuments')}
        setInvalid={() => {
          //bak
        }}
        onChange={handleOtherDocumentsChange}
        initialFiles={initialOtherDocumentsFiles}
        maxSize={MAX_SIZE - (totalSize - otherDocumentsFiles.reduce((acc, f) => acc + (f.size || 0), 0))}
        onPreview={handlePreviewServerFile}
        exceedCount={totalFiles >= MAX_NUMBER_OF_ATTACHMENTS}
        extra={
          <div className="Collapse" style={{ width: '100%', maxWidth: '100%', paddingLeft: 0, marginTop: 0 }}>
            {GetLifeDocFetchData?.lifeCIOtherDoc?.map((item, index) => {
              return (
                <div key={index} style={{ padding: '0 16px' }}>
                  <a
                    key={index}
                    href={isLocaleEnglish(i18n.language) ? item.docUrlEn || '' : item.docUrlTc || ''}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div>
                      <img src={PdfFileIcon} style={{ margin: '0  10px 5px 0', width: '24px', height: '24px' }} />
                      <span style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {isLocaleEnglish(i18n.language) ? item.docDescEn || '' : item.docDescTc || ''}
                      </span>
                    </div>
                  </a>
                </div>
              );
            })}
          </div>
        }
      />
      {totalFiles >= MAX_NUMBER_OF_ATTACHMENTS && <Text type="danger">{t('enquiry.error.noOfFilesExceeds')}</Text>}
    </Row>
  );
};
